import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useBreakpoint } from '~/hooks/useBreakpoint';

type AccordionMode = 'single' | 'independent';

type Props = {
  children: false | React.ReactElement | (false | React.ReactElement)[];
  /** Mode: 'single' = only one panel open at a time, 'independent' = each panel manages its own state */
  mode?: AccordionMode;
  /** Index of panel to start open (single mode) or array of indices (independent mode) */
  defaultOpen?: number | number[];
  /** Controlled state: Array of indices of panels that are open */
  openPanel?: number | number[];
  /** Callback when panel is toggled */
  onToggledPanel?: (idx?: number) => void;
};

type Ref = HTMLDivElement;

type Context = {
  mode: AccordionMode;
  openPanels: number[];
  toggle: (index: number) => void;
};

type TCardContext = {
  index: number;
  open: boolean;
  toggle: () => void;
};

const AccordionContext = createContext<Context>({} as Context);
const CardContext = createContext<TCardContext>({} as TCardContext);

AccordionContext.displayName = 'AccordionContext';
CardContext.displayName = 'CardContext';

const useAccordionContext = () => useContext(AccordionContext);
const useCardContext = () => useContext(CardContext);

// Simplified AccordionRoot component
const AccordionRoot = memo(function AccordionRoot({
  children,
  mode = 'single',
  defaultOpen,
  openPanel,
  onToggledPanel,
}: Props) {
  // Initialize state based on mode and props
  const getInitialState = useCallback(() => {
    if (openPanel !== undefined) {
      return Array.isArray(openPanel) ? openPanel : [openPanel];
    }

    if (defaultOpen !== undefined) {
      return Array.isArray(defaultOpen) ? defaultOpen : [defaultOpen];
    }

    // Default behavior based on mode
    if (mode === 'independent') {
      // In independent mode, start with all panels open
      const childCount = Children.count(children);
      return Array.from({ length: childCount }, (_, i) => i);
    } else {
      // In single mode, start with first panel open
      return [0];
    }
  }, [mode, defaultOpen, openPanel, children]);

  const [openPanels, setOpenPanels] = useState<number[]>(getInitialState);

  // Update state when controlled props change
  React.useEffect(() => {
    if (openPanel !== undefined) {
      const newOpenPanels = Array.isArray(openPanel) ? openPanel : [openPanel];
      setOpenPanels(newOpenPanels);
    }
  }, [openPanel]);

  // Toggle function based on mode
  const toggle = useCallback(
    (index: number) => {
      setOpenPanels((prev) => {
        let newOpenPanels: number[];

        if (mode === 'single') {
          // Single mode: only one panel open at a time
          newOpenPanels = prev.includes(index) ? [] : [index];
        } else {
          // Independent mode: toggle individual panels
          newOpenPanels = prev.includes(index)
            ? prev.filter((idx) => idx !== index)
            : [...prev, index].sort((a, b) => a - b);
        }

        // Call parent callback
        onToggledPanel?.(index);

        return newOpenPanels;
      });
    },
    [mode, onToggledPanel]
  );

  // Memoize context value
  const contextValue = useMemo(
    () => ({
      mode,
      openPanels,
      toggle,
    }),
    [mode, openPanels, toggle]
  );

  // Memoize children with indices
  const memoizedChildren = useMemo(() => {
    return Children.map(children, (child, index) =>
      child ? cloneElement(child, { index }) : null
    );
  }, [children]);

  return (
    <AccordionContext.Provider value={contextValue}>
      {memoizedChildren}
    </AccordionContext.Provider>
  );
});

// Memoize the Card component
const Card = memo(
  forwardRef<Ref, Props>(
    ({ children, index }: Props & { index?: number }, ref) => {
      const { openPanels, toggle } = useAccordionContext();

      // Memoize card context value
      const cardContextValue = useMemo(
        () => ({
          index: index ?? -1,
          open: openPanels.includes(index ?? -1),
          toggle: () => {
            if (index !== undefined) {
              toggle(index);
            }
          },
        }),
        [index, openPanels, toggle]
      );

      return (
        <CardContext.Provider value={cardContextValue}>
          <div
            ref={ref}
            className='border-1 shadow-[2px_2px_3px_0px_rgba(111, 80, 80, 0.1)] mb-5 rounded-lg border border-grey-20'
          >
            {children}
          </div>
        </CardContext.Provider>
      );
    }
  )
);

// Memoize the GreyCard component
const GreyCard = memo(function GreyCard({
  children,
  index,
  className,
  error,
  ...props
}: {
  children: React.ReactNode;
  index?: number;
  className?: string;
  error?: boolean;
  [key: string]: unknown;
}) {
  const { openPanels, toggle } = useAccordionContext();
  const { isMobile } = useBreakpoint();

  // Memoize card context value
  const cardContextValue = useMemo(
    () => ({
      index: index ?? -1,
      open: openPanels.includes(index ?? -1),
      toggle: () => {
        if (index !== undefined) {
          toggle(index);
        }
      },
    }),
    [index, openPanels, toggle]
  );

  const cardClasses = useMemo(() => {
    return classNames(
      'greycard border-1 rounded-lg border bg-surface-container-background shadow-[2px_2px_3px_0px_rgba(0,0,0,0.1)]',
      isMobile ? 'mb-[13px]' : 'mb-5',
      openPanels.includes(index ?? -1) && 'pb-2',
      error && !openPanels.includes(index ?? -1)
        ? 'border-red-500'
        : 'border-outline-variant',
      className
    );
  }, [className, error, isMobile, index, openPanels]);

  return (
    <CardContext.Provider value={cardContextValue}>
      <div {...props} className={cardClasses}>
        {children}
      </div>
    </CardContext.Provider>
  );
});

// Memoize the GreyButton component
const GreyButton = memo(function GreyButton({
  children,
  help,
  summary,
  dataUsetiful,
}: {
  children: React.ReactNode;
  help?: string;
  summary?: React.ReactNode;
  dataUsetiful?: string;
}) {
  const { open, toggle } = useCardContext();

  const buttonClasses = useMemo(() => {
    return classNames(
      'flex w-full items-center justify-between gap-4 rounded-lg text-left font-medium text-grey-90',
      open ? 'bg-surface-container-background' : 'bg-white'
    );
  }, [open]);

  return (
    <motion.button
      type='button'
      className={buttonClasses}
      onClick={() => toggle()}
      data-usetiful={dataUsetiful}
    >
      {children}
      {!open &&
        (summary ? (
          <span className='truncate font-normal'>{summary}</span>
        ) : (
          <span className='truncate font-normal text-grey-40'>{help}</span>
        ))}
    </motion.button>
  );
});

// Memoize the Button component
const Button = memo(function Button({
  children,
  help,
  summary,
  error,
  dataUsetiful,
}: {
  children: React.ReactNode;
  help?: string;
  summary?: React.ReactNode;
  error?: boolean;
  dataUsetiful?: string;
}) {
  const { open, toggle } = useCardContext();

  const buttonClasses = useMemo(() => {
    return classNames(
      'flex w-full justify-between gap-4 px-3 py-5 text-left font-semibold text-primary md:px-4',
      open && 'pb-0'
    );
  }, [open]);

  const childClasses = useMemo(() => {
    return open ? 'mb-4 text-lg' : 'text-md';
  }, [open]);

  return (
    <motion.button
      type='button'
      className={buttonClasses}
      onClick={() => toggle()}
      data-usetiful={dataUsetiful}
    >
      <div className={childClasses}>{children} </div>
      {!open &&
        (summary ? (
          <span
            className={classNames(
              'truncate ',
              error ? 'font-light text-red-500' : 'font-normal text-primary'
            )}
          >
            {summary}
          </span>
        ) : (
          <span
            className={classNames(
              'truncate font-light',
              error ? 'text-red-500' : 'text-tertiary'
            )}
          >
            {help}
          </span>
        ))}
    </motion.button>
  );
});

// Memoize the Panel component
const Panel = memo(function Panel({
  children,
  scroll,
  className,
}: {
  children: React.ReactNode | ((next: () => void) => React.ReactNode);
  /** If true sets a max height of the open panel and scrolls if necessary, otherwise should fit content */
  scroll?: boolean;
  className?: string;
}) {
  const { index, open } = useCardContext();
  const accordionContext = useAccordionContext();

  const next = useCallback(() => {
    if (index !== undefined && index >= 0) {
      accordionContext.toggle(index + 1);
    }
  }, [accordionContext, index]);

  const panelClasses = useMemo(() => {
    return classNames(className, 'px-3 md:px-4', {
      'max-h-64 overflow-y-auto': scroll,
    });
  }, [className, scroll]);

  const variants = useMemo(
    () => ({
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    }),
    []
  );

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.section
          key='content'
          initial='collapsed'
          animate='open'
          exit='collapsed'
          variants={variants}
          transition={{ duration: 0.25, ease: [0.04, 0.62, 0.23, 0.98] }}
          className={panelClasses}
        >
          {typeof children === 'function' ? children(next) : children}
          <div className='flex justify-end'>{/* skip/next buttons */}</div>
        </motion.section>
      )}
    </AnimatePresence>
  );
});

// Memoize the PanelSection component
const PanelSection = memo(function PanelSection({
  children,
  className,
  heading,
}: {
  children: React.ReactNode;
  className?: string;
  heading?: string | React.ReactNode;
}) {
  const sectionClasses = useMemo(() => {
    return classNames(
      'mx-0 mb-5 rounded-[5px] border border-outline-variant bg-white px-3 pb-3 pt-4 md:px-4',
      className,
      {
        'flex flex-col': heading,
      }
    );
  }, [className, heading]);

  return (
    <section className={sectionClasses}>
      {heading && (
        <div className='pb-3 text-base font-semibold text-primary'>
          {heading}
        </div>
      )}
      {children}
    </section>
  );
});

export const Accordion = Object.assign(AccordionRoot, {
  GreyCard,
  GreyButton,
  Card,
  Button,
  Panel,
  PanelSection,
});
